import type { Character } from "../types/entity.js";
import { findClassData } from "../chargen/classData.js";
import { DEATH_SAVE_NONE } from "../types/entity.js";
import { mintUuid } from "./partyIdentity.js";

/**
 * Reuse-party fork (P22).
 *
 * Takes a snapshot of a prior party's roster and produces a fresh, L1 clone:
 * identity fields (name, race, class, background, portrait, ability scores,
 * skill/save proficiencies) are preserved verbatim; everything that accrues
 * during play (HP, XP, spell slots, conditions, death saves, inventory —
 * inventory isn't on the Character record but is reset by virtue of the new
 * party not inheriting any equipment) is reset to its L1 value.
 *
 * Callers are responsible for minting the new `partyId` and calling
 * `PartyStore.setMembers` / `setPartyIdentity`. This helper is pure.
 *
 * Level 1 stats come from `CLASS_TABLE` — matches the chargen wizard path.
 * If a source character's class id isn't in the table (a roster-provided
 * custom class like Exit Velocity's psion, or a future LLM-authored class)
 * we fall back to the source's own `maxHp` and `armorClass` so the fork
 * still produces a usable record — and, when the source was a caster, to
 * the standard L1 caster loadout of two 1st-level slots so the clone can
 * still cast (every shipped caster class starts at {1: 2}).
 */
export function cloneRosterToL1(source: readonly Character[]): Character[] {
  return source.map(cloneCharacterToL1);
}

const FALLBACK_L1_CASTER_SLOTS: Record<number, number> = { 1: 2 };

function cloneCharacterToL1(src: Character): Character {
  const classData = findClassData(src.characterClass);
  const maxHp = classData?.maxHp ?? src.maxHp;
  const armorClass = classData?.ac ?? src.armorClass;
  // Known class → its table entry decides (fighter stays slotless). Unknown
  // class → infer from the source: slot *keys* mark a caster even when the
  // save caught them with every slot spent ({1: 0} still means caster).
  const srcWasCaster = Object.keys(src.spellSlots).length > 0;
  const spellSlots: Record<number, number> = classData
    ? { ...(classData.spellSlots ?? {}) }
    : srcWasCaster
      ? { ...FALLBACK_L1_CASTER_SLOTS }
      : {};

  return {
    kind: "player",
    instanceId: `pc-${mintUuid()}`,
    dataId: src.dataId,
    name: src.name,
    characterClass: src.characterClass,
    race: src.race,
    background: src.background,
    portrait: src.portrait,
    level: 1,
    position: { x: 0, z: 0 },
    hp: maxHp,
    maxHp,
    armorClass,
    speed: { ...src.speed },
    abilities: { ...src.abilities },
    proficiencyBonus: 2,
    spellSlots,
    ...(src.castingAbility !== undefined ? { castingAbility: src.castingAbility } : {}),
    xp: 0,
    knownSpells: [],
    reactions: [],
    skillProficiencies: new Set(src.skillProficiencies),
    saveProficiencies: new Set(src.saveProficiencies),
    expertiseSkills: new Set(),
    conditions: new Set(),
    deathSaves: DEATH_SAVE_NONE,
    formationSlot: src.formationSlot ?? null,
  };
}
